import { Injectable, inject, NgZone, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStateService } from './auth-state.service';
import { TokenService } from './token.service';
import { DialogService } from './dialog.service';
import { StorageIntegrityService } from './storage-integrity.service';

export interface SessionConfig {
  inactivityTimeoutMinutes: number;
  warningTimeMinutes: number;
  tokenRefreshWarningMinutes: number;
  enableAutoRefresh: boolean;
  enableInactivityWarning: boolean;
}

export interface SessionStatus {
  isActive: boolean;
  timeUntilInactivity: number;
  timeUntilTokenExpiry: number;
  lastActivity: Date | null;
  warningShown: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SessionManagementService {
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly authStateService = inject(AuthStateService);
  private readonly tokenService = inject(TokenService);
  private readonly dialogService = inject(DialogService);
  private readonly storageIntegrityService = inject(StorageIntegrityService);

  // Session configuration
  private config: SessionConfig = {
    inactivityTimeoutMinutes: 30,
    warningTimeMinutes: 5,
    tokenRefreshWarningMinutes: 5,
    enableAutoRefresh: true,
    enableInactivityWarning: true
  };

  // Session state
  private sessionStatus = signal<SessionStatus>({
    isActive: false,
    timeUntilInactivity: 0,
    timeUntilTokenExpiry: 0,
    lastActivity: null,
    warningShown: false
  });

  // Computed values
  readonly status = computed(() => this.sessionStatus());
  readonly isSessionActive = computed(() => this.sessionStatus().isActive);
  readonly shouldShowWarning = computed(() => {
    const status = this.sessionStatus();
    const warningTime = this.config.warningTimeMinutes * 60 * 1000;
    return status.isActive && status.timeUntilInactivity <= warningTime && status.timeUntilInactivity > 0;
  });

  // Timer management
  private inactivityTimer: any = null;
  private warningTimer: any = null;
  private tokenCheckTimer: any = null;
  private activityCheckTimer: any = null;

  // Activity tracking
  private readonly ACTIVITY_EVENTS = [
    'mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'
  ];

  // Warning state management
  private isWarningDisplayed = false;
  private isTokenWarningDisplayed = false;

  constructor() {
    this.setupActivityListeners();
  }

  /**
   * Start session management
   */
  start(customConfig?: Partial<SessionConfig>): void {
    if (this.isSessionActive()) {
      console.log('Session management already active');
      return;
    }

    // Update configuration if provided
    if (customConfig) {
      this.config = { ...this.config, ...customConfig };
    }

    console.log('Starting session management with config:', this.config);

    // Reset warning states
    this.isWarningDisplayed = false;
    this.isTokenWarningDisplayed = false;

    // Initialize session
    this.updateSessionStatus({
      isActive: true,
      lastActivity: new Date(),
      warningShown: false,
      timeUntilInactivity: this.config.inactivityTimeoutMinutes * 60 * 1000,
      timeUntilTokenExpiry: 0
    });

    // Start all timers
    this.startInactivityTimer();
    this.startTokenMonitoring();
    this.startActivityUpdater();

    console.log('Session management started successfully');
  }

  /**
   * Stop session management
   */
  stop(): void {
    if (!this.isSessionActive()) {
      return;
    }

    console.log('Stopping session management');

    // Clear all timers
    this.clearAllTimers();

    // Remove activity listeners
    this.removeActivityListeners();

    // Reset session status
    this.updateSessionStatus({
      isActive: false,
      timeUntilInactivity: 0,
      timeUntilTokenExpiry: 0,
      lastActivity: null,
      warningShown: false
    });

    // Reset warning states
    this.isWarningDisplayed = false;
    this.isTokenWarningDisplayed = false;

    console.log('Session management stopped');
  }

  /**
   * Reset session activity (called on user interaction)
   */
  resetActivity(): void {
    if (!this.isSessionActive()) {
      return;
    }

    const now = new Date();
    
    // Update last activity
    this.authStateService.updateLastActivity();
    
    // Update session status
    this.updateSessionStatus({
      lastActivity: now,
      warningShown: false,
      timeUntilInactivity: this.config.inactivityTimeoutMinutes * 60 * 1000
    });

    // Reset warning states
    this.isWarningDisplayed = false;

    // Restart inactivity timer
    this.startInactivityTimer();

    // Auto-refresh token if needed
    if (this.config.enableAutoRefresh) {
      this.checkAndRefreshToken();
    }
  }

  /**
   * Extend session (called when user chooses to continue)
   */
  extendSession(): void {
    if (!this.isSessionActive()) {
      return;
    }

    console.log('Extending session');
    
    // Reset activity
    this.resetActivity();
    
    // Hide any warnings
    this.hideWarnings();
    
    // Refresh current company token
    this.refreshCurrentCompanyToken();
  }

  /**
   * Get session configuration
   */
  getConfig(): SessionConfig {
    return { ...this.config };
  }

  /**
   * Update session configuration
   */
  updateConfig(newConfig: Partial<SessionConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Restart if currently active
    if (this.isSessionActive()) {
      this.stop();
      this.start();
    }
  }

  /**
   * Force logout due to inactivity
   */
  async forceLogout(): Promise<void> {
    console.log('Forcing logout due to inactivity');
    
    this.stop();
    
    // Show logout notification
    await this.showInactivityLogoutDialog();
    
    // Logout through auth service
    try {
      const authService = this.getAuthService();
      if (authService) {
        await authService.logout('/auth/login');
      } else {
        this.fallbackLogout();
      }
    } catch (error) {
      console.error('Error during forced logout:', error);
      this.fallbackLogout();
    }
  }

  /**
   * Private methods
   */

  /**
   * Start inactivity timer
   */
  private startInactivityTimer(): void {
    this.clearTimer('inactivity');
    
    const timeoutMs = this.config.inactivityTimeoutMinutes * 60 * 1000;
    const warningMs = this.config.warningTimeMinutes * 60 * 1000;
    
    // Set warning timer
    this.warningTimer = setTimeout(() => {
      this.showInactivityWarning();
    }, timeoutMs - warningMs);
    
    // Set logout timer
    this.inactivityTimer = setTimeout(() => {
      this.forceLogout();
    }, timeoutMs);
  }

  /**
   * Start token monitoring
   */
  private startTokenMonitoring(): void {
    this.clearTimer('token');
    
    // Check tokens every minute
    this.tokenCheckTimer = setInterval(() => {
      this.checkTokenStatus();
    }, 60000);
    
    // Initial check
    this.checkTokenStatus();
  }

  /**
   * Start activity status updater
   */
  private startActivityUpdater(): void {
    this.clearTimer('activity');
    
    // Update session status every 10 seconds
    this.activityCheckTimer = setInterval(() => {
      this.updateActivityStatus();
    }, 10000);
  }

  /**
   * Clear specific timer
   */
  private clearTimer(type: 'inactivity' | 'warning' | 'token' | 'activity'): void {
    switch (type) {
      case 'inactivity':
        if (this.inactivityTimer) {
          clearTimeout(this.inactivityTimer);
          this.inactivityTimer = null;
        }
        break;
      case 'warning':
        if (this.warningTimer) {
          clearTimeout(this.warningTimer);
          this.warningTimer = null;
        }
        break;
      case 'token':
        if (this.tokenCheckTimer) {
          clearInterval(this.tokenCheckTimer);
          this.tokenCheckTimer = null;
        }
        break;
      case 'activity':
        if (this.activityCheckTimer) {
          clearInterval(this.activityCheckTimer);
          this.activityCheckTimer = null;
        }
        break;
    }
  }

  /**
   * Clear all timers
   */
  private clearAllTimers(): void {
    this.clearTimer('inactivity');
    this.clearTimer('warning');
    this.clearTimer('token');
    this.clearTimer('activity');
  }

  /**
   * Setup activity listeners
   */
  private setupActivityListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      this.ACTIVITY_EVENTS.forEach(event => {
        window.addEventListener(event, this.onUserActivity.bind(this), { passive: true });
      });
    });
  }

  /**
   * Remove activity listeners
   */
  private removeActivityListeners(): void {
    this.ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, this.onUserActivity.bind(this));
    });
  }

  /**
   * Handle user activity
   */
  private onUserActivity(): void {
    if (this.isSessionActive()) {
      this.resetActivity();
    }
  }

  /**
   * Update session status
   */
  private updateSessionStatus(updates: Partial<SessionStatus>): void {
    const current = this.sessionStatus();
    this.sessionStatus.set({ ...current, ...updates });
  }

  /**
   * Update activity status for display
   */
  private updateActivityStatus(): void {
    if (!this.isSessionActive()) {
      return;
    }

    const now = new Date();
    const lastActivity = this.sessionStatus().lastActivity;
    
    if (lastActivity) {
      const timeSinceActivity = now.getTime() - lastActivity.getTime();
      const timeoutMs = this.config.inactivityTimeoutMinutes * 60 * 1000;
      const timeUntilInactivity = Math.max(0, timeoutMs - timeSinceActivity);
      
      // Get token expiry time for current company
      const currentCompany = this.authStateService.currentCompany();
      let timeUntilTokenExpiry = 0;
      
      if (currentCompany) {
        const validation = this.tokenService.validateToken(currentCompany);
        timeUntilTokenExpiry = validation.expiresIn;
      }
      
      this.updateSessionStatus({
        timeUntilInactivity,
        timeUntilTokenExpiry
      });
    }
  }

  /**
   * Check token status for all companies
   */
  private checkTokenStatus(): void {
    const currentCompany = this.authStateService.currentCompany();
    if (!currentCompany) {
      return;
    }

    const validation = this.tokenService.validateToken(currentCompany);

    if (validation.isExpired) {
      console.warn(`Token expired for company: ${currentCompany}`);
      if (this.config.enableAutoRefresh) {
        // Attempt a refresh first. AuthInterceptor may already be doing one;
        // TokenService deduplicates concurrent refresh calls so only one HTTP
        // request will be made regardless. We only force-logout if the refresh
        // itself fails (handled inside autoRefreshToken).
        this.autoRefreshToken(currentCompany);
      } else {
        this.showTokenExpiredDialog();
      }
    } else if (validation.shouldRefresh && this.config.enableAutoRefresh) {
      this.autoRefreshToken(currentCompany);
    }
  }

  /**
   * Auto-refresh token
   */
  private async autoRefreshToken(companyName: string): Promise<void> {
    // Guard: don't show another dialog if one is already up
    if (this.isTokenWarningDisplayed) {
      return;
    }
    try {
      console.log(`Auto-refreshing token for company: ${companyName}`);
      await this.tokenService.refreshToken(companyName);
    } catch (error) {
      console.error(`Auto-refresh failed for company ${companyName}:`, error);
      this.showTokenRefreshFailedDialog();
    }
  }

  /**
   * Check and refresh token if needed
   */
  private async checkAndRefreshToken(): Promise<void> {
    const currentCompany = this.authStateService.currentCompany();
    if (!currentCompany) {
      return;
    }

    const validation = this.tokenService.validateToken(currentCompany);
    if (validation.shouldRefresh) {
      await this.autoRefreshToken(currentCompany);
    }
  }

  /**
   * Refresh current company token
   */
  private async refreshCurrentCompanyToken(): Promise<void> {
    const currentCompany = this.authStateService.currentCompany();
    if (!currentCompany) {
      return;
    }

    try {
      await this.tokenService.refreshToken(currentCompany);
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
  }

  /**
   * Show inactivity warning dialog using DialogService
   */
  private async showInactivityWarning(): Promise<void> {
    if (this.isWarningDisplayed || !this.config.enableInactivityWarning) {
      return;
    }

    this.isWarningDisplayed = true;
    this.updateSessionStatus({ warningShown: true });

    this.ngZone.run(async () => {
      try {
        const timeRemaining = this.config.warningTimeMinutes * 60 * 1000;
        const shouldContinue = await this.dialogService.showSessionWarning(timeRemaining);

        if (shouldContinue) {
          this.extendSession();
        } else {
          this.forceLogout();
        }
      } catch (error) {
        console.error('Error showing inactivity warning:', error);
        // Fallback behavior
        this.forceLogout();
      } finally {
        this.isWarningDisplayed = false;
      }
    });
  }

  /**
   * Show inactivity logout dialog using DialogService
   */
  private async showInactivityLogoutDialog(): Promise<void> {
    this.ngZone.run(async () => {
      try {
        await this.dialogService.showSessionExpired();
      } catch (error) {
        console.error('Error showing logout dialog:', error);
      }
    });
  }

  /**
   * Show token expired dialog using DialogService
   */
  private async showTokenExpiredDialog(): Promise<void> {
    if (this.isTokenWarningDisplayed) {
      return;
    }

    this.isTokenWarningDisplayed = true;

    this.ngZone.run(async () => {
      try {
        await this.dialogService.showTokenExpired();
        this.forceLogout();
      } catch (error) {
        console.error('Error showing token expired dialog:', error);
        this.forceLogout();
      } finally {
        this.isTokenWarningDisplayed = false;
      }
    });
  }

  /**
   * Show token refresh failed dialog using DialogService
   */
  private async showTokenRefreshFailedDialog(): Promise<void> {
    this.ngZone.run(async () => {
      try {
        const shouldTryAgain = await this.dialogService.showTokenRefreshWarning();
        
        if (shouldTryAgain) {
          this.refreshCurrentCompanyToken();
        } else {
          this.forceLogout();
        }
      } catch (error) {
        console.error('Error showing token refresh failed dialog:', error);
        this.forceLogout();
      }
    });
  }

  /**
   * Hide all warnings
   */
  private hideWarnings(): void {
    this.isWarningDisplayed = false;
    this.isTokenWarningDisplayed = false;
    this.updateSessionStatus({ warningShown: false });
  }

  /**
   * Get auth service (lazy injection to avoid circular dependency)
   */
  private getAuthService(): any {
    try {
      // This would need to be injected properly in a real implementation
      // For now, this is a placeholder
      return null;
    } catch (error) {
      console.error('Could not get AuthService:', error);
      return null;
    }
  }

  /**
   * Fallback logout
   */
  private fallbackLogout(): void {
    // Clear integrity snapshot BEFORE wiping localStorage to prevent
    // concurrent requests from detecting a false "tampering" event.
    this.storageIntegrityService.clearState();

    // Clear storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Navigate to login
    this.router.navigate(['/auth/login']);
  }

  /**
   * Get formatted time until inactivity
   */
  getFormattedTimeUntilInactivity(): string {
    const timeMs = this.sessionStatus().timeUntilInactivity;
    const minutes = Math.floor(timeMs / (1000 * 60));
    const seconds = Math.floor((timeMs % (1000 * 60)) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Check if warning should be shown
   */
  shouldShowInactivityWarning(): boolean {
    return this.shouldShowWarning() && !this.isWarningDisplayed;
  }
}
